import React, { useEffect, useId, useState } from 'react';
import DataTableColumnVisibility from './DataTableColumnVisibility';
import DataTableFilterIndicators from './DataTableFilterIndicators';

/* eslint-disable react/prop-types */
/**
 * @template {import('@tanstack/react-table').RowData} TData
 * @param {{ table: import('@tanstack/react-table').Table<TData>, enableGlobalFilter: boolean, enableColumnVisibility: boolean, globalFilter: string, onGlobalFilterChange: (value: string) => void, searchPlaceholder: string, startContent: import('react').ReactNode, actions: import('react').ReactNode, clearGlobalFilterOnClear: boolean }} props
 */
const DataTableToolbar = ({
  table,
  enableGlobalFilter,
  enableColumnVisibility,
  globalFilter,
  onGlobalFilterChange,
  searchPlaceholder,
  startContent,
  actions,
  clearGlobalFilterOnClear,
}) => {
  const [searchValue, setSearchValue] = useState(globalFilter);
  const searchId = useId();
  const hasVisibilityOptions = enableColumnVisibility
    && table.getAllLeafColumns().some((column) => column.getCanHide());

  useEffect(() => {
    setSearchValue(globalFilter);
  }, [globalFilter]);

  useEffect(() => {
    if (searchValue === globalFilter) return undefined;
    const timeout = setTimeout(() => onGlobalFilterChange(searchValue), 300);
    return () => clearTimeout(timeout);
  }, [globalFilter, onGlobalFilterChange, searchValue]);

  const hasColumnFilters = table.getState().columnFilters.length > 0;
  const hasGlobalFilter = Boolean(globalFilter.trim());
  if (!startContent && !enableGlobalFilter && !hasVisibilityOptions && !actions && !hasColumnFilters && !hasGlobalFilter) return null;

  return (
    <div className="mb-3 flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      {startContent && <div className="min-w-0 shrink-0">{startContent}</div>}
      <div className="flex min-w-0 flex-1 items-center gap-2">
        {enableGlobalFilter && (
          <label className="sr-only" htmlFor={searchId}>
            Search table
          </label>
        )}
        {enableGlobalFilter && (
          <input
            id={searchId}
            type="search"
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
            placeholder={searchPlaceholder}
            className="h-9 w-full min-w-0 max-w-sm rounded-md border border-gray-200 bg-white px-3 text-sm text-gray-700 outline-none placeholder:text-gray-400 focus:border-brand focus:ring-2 focus:ring-brand/20"
          />
        )}
      </div>
      <div className="flex w-full min-w-0 flex-wrap items-center justify-end gap-2 sm:w-auto sm:flex-nowrap">
        {actions}
        {hasVisibilityOptions && <DataTableColumnVisibility table={table} />}
      </div>
      <div className="w-full">
        <DataTableFilterIndicators
          table={table}
          globalFilter={globalFilter}
          clearGlobalFilterOnClear={clearGlobalFilterOnClear}
        />
      </div>
    </div>
  );
};

export default DataTableToolbar;
